import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import QRCode from "react-qr-code";
import { api } from "@/lib/api";
import { useAuth } from "@/components/AuthProvider";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2, ShieldCheck, Copy, CheckCircle2 } from "lucide-react";

const loadCheckout = () =>
  new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });

const inputCls = "w-full border-b border-[#27272A] bg-transparent py-4 text-base text-white placeholder-zinc-600 outline-none transition-colors duration-300 focus:border-white rounded-none";

export const BuyButton = ({ course }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [config, setConfig] = useState({ upi_id: "", payments: false });
  const [form, setForm] = useState({ name: "", email: "", phone: "", utr: "" });
  const { user } = useAuth();
  const navigate = useNavigate();

  const openDialog = async () => {
    if (user) {
      setForm((f) => ({ ...f, name: f.name || user.name || "", email: f.email || user.email || "" }));
    }
    setOpen(true);
    try {
      const { data } = await api.get("/");
      setConfig({ upi_id: data.upi_id || "", payments: !!data.payments });
    } catch {}
  };

  const upiLink = `upi://pay?pa=${config.upi_id}&pn=${encodeURIComponent("PP Academy")}&am=${course.price}&cu=INR&tn=${encodeURIComponent(course.title.slice(0, 28))}`;

  const copyUpi = async () => {
    try {
      await navigator.clipboard.writeText(config.upi_id);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {}
  };

  const submitUpi = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/payments/upi", { course_slug: course.slug, ...form });
      toast.success("Details received. Your course unlocks as soon as the UPI payment is verified.");
      setOpen(false);
      navigate(`/access/${data.access_token}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit your details. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const payStripe = async () => {
    if (!form.name || !form.email || !form.phone) {
      toast.error("Fill your name, email and phone first.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/payments/stripe-checkout", {
        course_slug: course.slug,
        name: form.name,
        email: form.email,
        phone: form.phone,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Card checkout is unavailable right now — please use UPI.");
      setLoading(false);
    }
  };

  const payRazorpay = async () => {
    if (!form.name || !form.email || !form.phone) {
      toast.error("Fill your name, email and phone first.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/payments/create-order", { course_slug: course.slug, name: form.name, email: form.email, phone: form.phone });
      const ok = await loadCheckout();
      if (!ok) throw new Error("checkout");
      const rzp = new window.Razorpay({
        key: data.key_id,
        amount: data.amount,
        currency: data.currency,
        name: "PP Academy",
        description: data.course_title,
        order_id: data.order_id,
        prefill: { name: form.name, email: form.email, contact: form.phone },
        theme: { color: "#FF4500" },
        handler: async (resp) => {
          try {
            await api.post("/payments/verify", {
              course_slug: course.slug,
              access_token: data.access_token,
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            toast.success("Payment confirmed. Welcome aboard.");
            navigate(`/access/${data.access_token}`);
          } catch {
            toast.error("Payment verification failed. Contact us and we'll sort it out.");
          }
        },
      });
      rzp.on("payment.failed", () => toast.error("Payment failed. No money was deducted for a failed attempt."));
      rzp.open();
      setOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start checkout. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <button
        data-testid="buy-course-button"
        onClick={openDialog}
        className="flex w-full items-center justify-center gap-3 bg-brand px-8 py-4 text-sm font-bold uppercase tracking-widest text-white transition-transform duration-300 hover:scale-95 focus-visible:ring-2 focus-visible:ring-brand"
      >
        Buy Now — ₹{course.price.toLocaleString("en-IN")}
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto border-white/10 bg-[#111111] text-white sm:max-w-md" data-testid="checkout-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-bold tracking-tight">Pay & enrol instantly.</DialogTitle>
          </DialogHeader>
          <a
            href={upiLink}
            data-testid="upi-app-pay-button"
            className="flex w-full items-center justify-center gap-3 bg-brand px-8 py-4 text-sm font-bold uppercase tracking-widest text-white transition-transform duration-300 hover:scale-95"
          >
            Pay ₹{course.price.toLocaleString("en-IN")} in UPI App
          </a>
          <p className="-mt-2 text-center text-xs text-zinc-500">Opens GPay / PhonePe / Paytm with the amount pre-filled (on your phone)</p>
          <div className="flex flex-col items-center gap-4 border border-white/10 bg-[#0a0a0a] p-6">
            <div className="bg-white p-3" data-testid="upi-qr">
              {config.upi_id ? <QRCode value={upiLink} size={150} /> : <div className="h-[150px] w-[150px] animate-pulse bg-zinc-200" />}
            </div>
            <div className="text-center">
              <div className="text-xs uppercase tracking-widest text-zinc-500">On desktop? Scan the QR, or pay directly to</div>
              <div className="mt-2 flex items-center justify-center gap-2">
                <span className="font-mono text-sm text-white" data-testid="upi-id-text">{config.upi_id || "loading..."}</span>
                <button type="button" data-testid="upi-copy-button" onClick={copyUpi} className="text-zinc-400 transition-colors duration-200 hover:text-brand" aria-label="Copy UPI ID">
                  {copied ? <CheckCircle2 size={16} className="text-emerald-500" /> : <Copy size={16} />}
                </button>
              </div>
            </div>
          </div>
          <p className="text-xs leading-relaxed text-zinc-500">
            1. Pay with the button above or the QR. 2. Fill the form with your UTR (transaction reference from your UPI app). 3. Your course unlocks instantly — no waiting.
          </p>
          <form onSubmit={submitUpi} className="mt-2 flex flex-col gap-6" data-testid="checkout-form">
            <input data-testid="checkout-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Full name" className={inputCls} />
            <input data-testid="checkout-email-input" required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email address" className={inputCls} />
            <input data-testid="checkout-phone-input" required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Phone number" className={inputCls} />
            <input data-testid="checkout-utr-input" required value={form.utr} onChange={(e) => setForm({ ...form, utr: e.target.value })} placeholder="UTR / transaction reference number" className={inputCls} />
            <button
              data-testid="upi-submit-button"
              type="submit"
              disabled={loading}
              className="mt-2 flex items-center justify-center gap-3 bg-brand px-8 py-4 text-sm font-bold uppercase tracking-widest text-white transition-transform duration-300 hover:scale-95 disabled:opacity-50"
            >
              {loading && <Loader2 size={16} className="animate-spin" />}
              {loading ? "Enrolling..." : "Get Instant Access"}
            </button>
          </form>
          <div className="flex items-center gap-3 text-xs uppercase tracking-widest text-zinc-600">
            <span className="h-px flex-1 bg-white/10" />
            or pay by card
            <span className="h-px flex-1 bg-white/10" />
          </div>
          <button
            type="button"
            data-testid="stripe-pay-button"
            onClick={payStripe}
            disabled={loading}
            className="flex w-full items-center justify-center gap-3 border border-white/20 px-8 py-4 text-sm font-bold uppercase tracking-widest text-white transition-colors duration-300 hover:border-brand disabled:opacity-50"
          >
            Pay by Card — Stripe
          </button>
          {config.payments && (
            <button
              type="button"
              data-testid="razorpay-pay-button"
              onClick={payRazorpay}
              disabled={loading}
              className="mt-2 flex w-full items-center justify-center gap-3 border border-white/20 px-8 py-4 text-sm font-bold uppercase tracking-widest text-white transition-colors duration-300 hover:border-brand disabled:opacity-50"
            >
              Or pay instantly via Razorpay
            </button>
          )}
          <p className="flex items-center gap-2 text-xs text-zinc-600">
            <ShieldCheck size={14} /> Direct UPI to {config.upi_id ? "the mentor" : "PP Academy"} · instant enrolment
          </p>
        </DialogContent>
      </Dialog>
    </>
  );
};
